import { Stack } from 'component-library';
import { Trans, useTranslation } from 'react-i18next';

import { IndicatorName } from '@/api/rest/resources/types/fact';
import { CapsuleTrend } from '@/components';
import { NCCardSectionHeader, printLatestDate, useNCCardContext } from '@/components/NCCard/NCCard';
import {
  getNCChangeIcon,
  getNCChangeIconVariant,
  NCFactCapsule,
  NCFactCapsuleProps,
} from '@/components/NCFactCapsule/NCFactCapsule';
import { useDisplayNumber } from '@/hooks/useDisplayNumber';
import { useFact } from '@/hooks/useFact';
import { useOrganizationIndicatorReportingFact } from '@/pages/shared/hooks/useOrganizationFact';
import { isNullish } from '@/utils/booleans';
import { formatFactKilogramsToTonnes } from '@/utils/formatting/formatKgToTon';

export const CarbonStatsTile = () => {
  return (
    <Stack className='gap-8 p-6 md:justify-center md:gap-4 md:p-8 md:pr-2' data-testid='carbon-stats-tile'>
      <BelowGroundCarbonStorage />
    </Stack>
  );
};

const BelowGroundCarbonStorage = () => {
  const { t } = useTranslation();
  const { analysisType } = useNCCardContext();

  const carbonBg1YearUpliftPotentialFact =
    analysisType === 'total'
      ? IndicatorName.permanent_soil_carbon_1y_uplift_potential_total_change
      : IndicatorName.permanent_soil_carbon_1y_uplift_potential_per_ha_change;
  const { [carbonBg1YearUpliftPotentialFact]: carbonBg1YearUpliftPotential } =
    useOrganizationIndicatorReportingFact<number>(carbonBg1YearUpliftPotentialFact);
  const carbonBg1YearUpliftPotentialFormatted = formatFactKilogramsToTonnes(carbonBg1YearUpliftPotential);

  const carbonBg1YearTrendFact =
    analysisType === 'total'
      ? IndicatorName.permanent_soil_carbon_1y_uplift_potential_total_pct_change
      : IndicatorName.permanent_soil_carbon_1y_uplift_potential_per_ha_pct_change;
  const { [carbonBg1YearTrendFact]: carbonBg1YearTrend } =
    useOrganizationIndicatorReportingFact<number>(carbonBg1YearTrendFact);

  const carbonBg10YearUpliftPotentialFact =
    analysisType === 'total'
      ? IndicatorName.permanent_soil_carbon_10y_uplift_potential_total_change
      : IndicatorName.permanent_soil_carbon_10y_uplift_potential_per_ha_change;
  const { [carbonBg10YearUpliftPotentialFact]: carbonBg10YearUpliftPotential } =
    useOrganizationIndicatorReportingFact<number>(carbonBg10YearUpliftPotentialFact);
  const carbonBg10YearUpliftPotentialFormatted = formatFactKilogramsToTonnes(carbonBg10YearUpliftPotential);

  const carbonBg10YearTrendFact =
    analysisType === 'total'
      ? IndicatorName.permanent_soil_carbon_10y_uplift_potential_total_pct_change
      : IndicatorName.permanent_soil_carbon_10y_uplift_potential_per_ha_pct_change;
  const { [carbonBg10YearTrendFact]: carbonBg10YearTrend } =
    useOrganizationIndicatorReportingFact<number>(carbonBg10YearTrendFact);

  const equivalentExplainer = useEquivalentsExplainer();

  const popover1Year: NCFactCapsuleProps['infoPopoverProps'] = equivalentExplainer
    ? {
        title: t('shared.ncaDetail.details.carbon.explainers.1YearPotential.title'),
        body: `${t('shared.ncaDetail.details.carbon.explainers.1YearPotential.body')}\n${equivalentExplainer}`,
        popoverTriggerProps: {
          'data-testid': 'carbon-bg-1-year-uplift-potential-popover',
        },
      }
    : undefined;

  const popover10Year: NCFactCapsuleProps['infoPopoverProps'] = equivalentExplainer
    ? {
        title: t('shared.ncaDetail.details.carbon.explainers.10YearPotential.title'),
        body: `${t('shared.ncaDetail.details.carbon.explainers.10YearPotential.body')}\n${equivalentExplainer}`,
        popoverTriggerProps: {
          'data-testid': 'carbon-bg-10-year-uplift-potential-popover',
        },
      }
    : undefined;

  const latestAnalysisDate = printLatestDate([
    carbonBg1YearUpliftPotentialFormatted?.measured_at,
    carbonBg10YearUpliftPotentialFormatted?.measured_at,
  ]);

  const pctUnit = t('global.ui.unit.%');
  let carbonBg1YearTrendDisplay = `${useDisplayNumber(carbonBg1YearTrend?.value ?? 0, { signDisplay: 'always' })} ${pctUnit}`;
  carbonBg1YearTrendDisplay = !isNullish(carbonBg1YearTrend?.value) ? carbonBg1YearTrendDisplay : '-';

  let carbonBg10YearTrendDisplay = `${useDisplayNumber(carbonBg10YearTrend?.value ?? 0, {
    signDisplay: 'always',
  })} ${pctUnit}`;
  carbonBg10YearTrendDisplay = !isNullish(carbonBg10YearTrend?.value) ? carbonBg10YearTrendDisplay : '-';

  return (
    <Stack className='gap-8 lg:mt-4 lg:justify-center lg:gap-6'>
      <NCCardSectionHeader
        title={t('shared.ncaDetail.details.carbon.labels.upliftPotentialForCarbonStorage')}
        supplementaryInfo={`${t('shared.ncaDetail.details.labels.latestAnalysis')} ${latestAnalysisDate}`}
        popover={{
          title: t('shared.ncaDetail.details.carbon.explainers.upliftPotentialForBelowTheGroundCarbonStorage.title'),
          body: t('shared.ncaDetail.details.carbon.explainers.upliftPotentialForBelowTheGroundCarbonStorage.body'),
        }}
      />
      <Stack direction='row' className='flex-wrap gap-16'>
        <NCFactCapsule
          label={t('shared.ncaDetail.details.labels.1YearPotential')}
          fact={carbonBg1YearUpliftPotentialFormatted}
          capsuleIconProps={{
            icon: getNCChangeIcon(carbonBg1YearUpliftPotentialFormatted),
            variant: getNCChangeIconVariant(carbonBg1YearUpliftPotentialFormatted),
          }}
          infoPopoverProps={popover1Year}
          trend={
            typeof carbonBg1YearTrend?.value === 'number' && (
              <CapsuleTrend variant={carbonBg1YearTrend.value < 0 ? 'negative' : 'positive'}>
                {carbonBg1YearTrendDisplay}
              </CapsuleTrend>
            )
          }
        />
        <NCFactCapsule
          label={t('shared.ncaDetail.details.labels.10YearPotential')}
          fact={carbonBg10YearUpliftPotentialFormatted}
          capsuleIconProps={{
            icon: getNCChangeIcon(carbonBg10YearUpliftPotentialFormatted),
            variant: getNCChangeIconVariant(carbonBg10YearUpliftPotentialFormatted),
          }}
          infoPopoverProps={popover10Year}
          trend={
            typeof carbonBg10YearTrend?.value === 'number' && (
              <CapsuleTrend variant={carbonBg10YearTrend.value < 0 ? 'negative' : 'positive'}>
                {carbonBg10YearTrendDisplay}
              </CapsuleTrend>
            )
          }
        />
      </Stack>
      {typeof carbonBg10YearTrend?.value === 'number' && (
        <Stack className='mt-4 gap-4 rounded-2xl bg-neutral-black-4 px-4 py-6'>
          <span className='typography-body1Bold'>
            {t('shared.ncaDetail.details.carbon.explainers.carbonUpliftPotential.title')}
          </span>
          <Trans
            i18nKey='shared.ncaDetail.details.carbon.explainers.carbonUpliftPotential.body'
            values={{
              trendPercent: carbonBg10YearTrendDisplay,
            }}
            components={{
              p: <p className='typography-body1 text-text-secondary' />,
            }}
          />
        </Stack>
      )}
    </Stack>
  );
};

// TODO: MVP-3284 Verify the explainer copy
const useEquivalentsExplainer = () => {
  const { t } = useTranslation();

  const { analysisType } = useNCCardContext();

  const carbonStorageBgFact =
    analysisType === 'total' ? IndicatorName.permanent_soil_carbon_total : IndicatorName.permanent_soil_carbon_per_ha;
  const { [carbonStorageBgFact]: carbonStorageBg } = useOrganizationIndicatorReportingFact(carbonStorageBgFact);

  const carbonStorageBgFormatted = formatFactKilogramsToTonnes(carbonStorageBg);

  const carbonStorageBgCo2EquivalentFact =
    analysisType === 'total'
      ? IndicatorName.permanent_soil_co2_equivalent_total
      : IndicatorName.permanent_soil_co2_equivalent_per_ha;
  const { [carbonStorageBgCo2EquivalentFact]: carbonStorageBgCo2Equivalent } = useOrganizationIndicatorReportingFact(
    carbonStorageBgCo2EquivalentFact,
  );

  const carbonStorageBgCo2EquivalentFormatted = formatFactKilogramsToTonnes(carbonStorageBgCo2Equivalent);

  const { display: carbonStorageBgDisplay, value: carbonStorageBgValue } = useFact(carbonStorageBgFormatted);

  const { display: carbonStorageBgCo2EquivalentDisplay, value: carbonStorageBgCo2EquivalentValue } = useFact(
    carbonStorageBgCo2EquivalentFormatted,
  );

  if (!carbonStorageBgValue || !carbonStorageBgCo2EquivalentValue) return undefined;

  return t('shared.ncaDetail.details.carbon.explainers.equivalents', {
    cValue: carbonStorageBgDisplay,
    co2Value: carbonStorageBgCo2EquivalentDisplay,
  });
};
